import { ConsoleLogger } from "@aws-amplify/core"
import {
  confirmResetPassword,
  confirmSignIn as confirmSignInV6,
  confirmSignUp as confirmSignUpV6,
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  resendSignUpCode,
  resetPassword,
  signIn as signInV6,
  signOut as signOutV6,
  signUp as signUpV6,
  updatePassword,
} from "aws-amplify/auth"

type AnyObj = Record<string, any>

const toError = (error: any) => {
  if (error && !error.code && error.name) {
    error.code = error.name
  }
  return error
}

const toLegacyChallengeName = (signInStep?: string): string | undefined => {
  if (
    signInStep === "CONFIRM_SIGN_IN_WITH_SMS_CODE" ||
    signInStep === "CONFIRM_SIGN_IN_WITH_EMAIL_CODE"
  ) {
    return "SMS_MFA"
  }
  if (signInStep === "CONFIRM_SIGN_IN_WITH_TOTP_CODE") {
    return "SOFTWARE_TOKEN_MFA"
  }
  return undefined
}

const getLegacyCurrentUser = async () => {
  const [user, attributes] = await Promise.all([
    getCurrentUser(),
    fetchUserAttributes(),
  ])
  return { ...user, attributes }
}

export const Logger = ConsoleLogger

export const Auth = {
  async signIn(username: string, password?: string) {
    try {
      const result = await signInV6({ username, password })
      return {
        ...result,
        challengeName: toLegacyChallengeName(result?.nextStep?.signInStep),
      }
    } catch (error) {
      throw toError(error)
    }
  },

  async confirmSignIn(_user: AnyObj, challengeResponse: string) {
    try {
      const result = await confirmSignInV6({ challengeResponse })
      return {
        ...result,
        challengeName: toLegacyChallengeName(result?.nextStep?.signInStep),
      }
    } catch (error) {
      throw toError(error)
    }
  },

  async signOut() {
    try {
      return await signOutV6()
    } catch (error) {
      throw toError(error)
    }
  },

  async signUp(input: AnyObj) {
    try {
      const result = await signUpV6({
        username: input?.username,
        password: input?.password,
        options: {
          userAttributes: input?.attributes,
        },
      })
      return {
        userConfirmed: result?.isSignUpComplete ?? false,
        userSub: result?.userId,
        ...result,
      }
    } catch (error) {
      throw toError(error)
    }
  },

  async confirmSignUp(
    username: string,
    confirmationCode: string,
    _password?: string,
  ) {
    try {
      return await confirmSignUpV6({ username, confirmationCode })
    } catch (error) {
      throw toError(error)
    }
  },

  async resendSignUp(username: string) {
    try {
      return await resendSignUpCode({ username })
    } catch (error) {
      throw toError(error)
    }
  },

  async forgotPassword(username: string) {
    try {
      return await resetPassword({ username })
    } catch (error) {
      throw toError(error)
    }
  },

  async forgotPasswordSubmit(
    username: string,
    confirmationCode: string,
    newPassword: string,
  ) {
    try {
      return await confirmResetPassword({
        username,
        confirmationCode,
        newPassword,
      })
    } catch (error) {
      throw toError(error)
    }
  },

  async currentAuthenticatedUser() {
    try {
      return await getLegacyCurrentUser()
    } catch (error) {
      throw toError(error)
    }
  },

  async changePassword(_user: AnyObj, oldPassword: string, newPassword: string) {
    try {
      return await updatePassword({ oldPassword, newPassword })
    } catch (error) {
      throw toError(error)
    }
  },

  async currentSession() {
    try {
      const session = await fetchAuthSession()
      return {
        getIdToken: () => ({
          getJwtToken: () => session?.tokens?.idToken?.toString?.() || "",
        }),
      }
    } catch (error) {
      throw toError(error)
    }
  },
}
